import { useTranslation } from "react-i18next";
import HeaderView from "../../../../components/admin/headerView";
import { useGHContext } from "../contexts/groupHierarchy.context";
import { AdminFormFieldSubmit } from "../../../../components/admin/formFields";
import { useDispatch, useSelector } from "react-redux";
import {
  setSelectedGroupIsModified,
  setModalsData,
  TModalsState,
} from "../../../../api/store/commonSlice";
import { APP_CONFIG } from "../../../../constants/constants";
import { useHGSelectorContext } from "../../../../components/common/hierarchyGroupSelector.context";
import { useEffect, useState } from "react";
import Tooltip from "../../../../components/cameraAdmin/tooltip";

type GroupHierarchyHeaderViewProps = {};

const GroupHierarchyHeaderView = (props: GroupHierarchyHeaderViewProps) => {
  const { t: tMain } = useTranslation();
  const { t: tGroups } = useTranslation("translation", {
    keyPrefix: "admins.groups",
  });
  const { t } = useTranslation("translation", {
    keyPrefix: "admins.groups.hierarchy",
  });

  const modalsState = useSelector(
    (state: { commonReducer: { modals: TModalsState } }) =>
      state.commonReducer.modals
  );
  const dispatch = useDispatch();

  const {
    movingGroup,
    setMovingGroup,
    moveToGroup,
    setMoveToGroup,
    parentOfMovingGroup,
    setParentOfMovingGroup,
  } = useGHContext();

  const { hGSelectorMode, selectedHGroups, setSelectedHGroups, selectLimit } =
    useHGSelectorContext();
  const isSelectedGroupModified = useSelector(
    (state: { commonReducer: { isSelectedGroupModified: boolean } }) =>
      state.commonReducer.isSelectedGroupModified
  );

  // State to store the initial selected groups
  const [initialSelectedHGroups, setInitialSelectedHGroups] = useState<
    string[]
  >([]);

  //Reset the flag
  useEffect(() => {
    dispatch(setSelectedGroupIsModified(false));
  }, []);

  // Capture initial selected groups
  useEffect(() => {
    if (
      initialSelectedHGroups.length === 0 &&
      selectedHGroups.length > 0 &&
      !isSelectedGroupModified
    ) {
      setInitialSelectedHGroups([...selectedHGroups]);
    }
  }, [initialSelectedHGroups, selectedHGroups, isSelectedGroupModified]);

  return (
    <HeaderView
      title={
        modalsState.showReadOnlyViewForHierarchyGroup
          ? t("view_heading", {
              group: modalsState.showReadOnlyViewForHierarchyGroup.groupid,
            })
          : hGSelectorMode
            ? t("select_heading")
            : tGroups("heading")
      }
      wrapperClassName={`flex justify-between ${APP_CONFIG.DES.DASH.P_HORIZ} py-2 group-[]/hierarchy-modal:p-0`}
      rightComponent={
        <div className="flex items-center gap-2">
          {/* Header area for groups selection or view group mode */}
          {!!hGSelectorMode && (
            <>
              {modalsState.showReadOnlyViewForHierarchyGroup ? (
                // Viewing group
                <div className="flex justify-end gap-2">
                  <AdminFormFieldSubmit
                    type="button"
                    variant="primary"
                    label={tMain("close")}
                    onClick={() => {
                      dispatch(
                        setModalsData({
                          ...modalsState,
                          showReadOnlyViewForHierarchyGroup: null,
                          showHierarchyGroupSelector: false,
                        })
                      );
                    }}
                  />
                </div>
              ) : (
                // Selecting groups
                <div className="flex justify-end gap-2">
                  <p className="mr-2 flex flex-col justify-center text-right">
                    <span className="font-bold text-lg leading-5 text-accent-blue-dark">
                      {!!selectLimit
                        ? t("selected_h_groups_count_w_limit", {
                            count: selectedHGroups.length,
                            limit: selectLimit,
                          })
                        : t("selected_h_groups_count", {
                            count: selectedHGroups.length,
                          })}
                    </span>
                    <span className="text-heading-gray888 text-xs leading-4 mt-1">
                      {selectedHGroups.slice(0, 3).map((group, index) => (
                        <span
                          key={index}
                          className="inline-block text-[10px] bg-accent-blue-paleO66 px-2 rounded-md text-accent-blue-dark ms-1"
                        >
                          {group}
                        </span>
                      ))}
                      {selectedHGroups.length > 3 && (
                        <span className="text-[10px]">{` ${t("plus_n_more", { count: selectedHGroups.length - 3 })}`}</span>
                      )}
                    </span>
                  </p>
                  <AdminFormFieldSubmit
                    type="button"
                    variant="primary"
                    label={tMain("confirm")}
                    onClick={() => {
                      dispatch(
                        setModalsData({
                          ...modalsState,
                          showHierarchyGroupSelector: false,
                        })
                      );
                    }}
                  />
                  <AdminFormFieldSubmit
                    type="button"
                    variant="danger-transparent"
                    label={t("clear")}
                    onClick={() => {
                      setSelectedHGroups([]);
                    }}
                  />
                  <AdminFormFieldSubmit
                    type="button"
                    variant="secondary"
                    label={tMain("cancel")}
                    onClick={() => {
                      setSelectedHGroups([...initialSelectedHGroups]); //Restoring initial groups
                      setTimeout(() => {
                        dispatch(
                          setModalsData({
                            ...modalsState,
                            showHierarchyGroupSelector: false,
                          })
                        );
                      }, 0);
                    }}
                  />
                </div>
              )}
            </>
          )}

          {/* Header area for moving group */}
          {!!movingGroup && (
            <>
              <p className="flex items-center mr-2">
                <span className="mr-2 mt-2">
                  <Tooltip description={t("move_info")} />
                </span>
                <span className="font-semibold text-base-text">
                  {t("moving_label")}
                </span>{" "}
                <span className="font-medium text-accent-blue-dark ml-1">
                  {movingGroup}
                </span>
              </p>
              <AdminFormFieldSubmit
                type="button"
                label={t("confirm_move_button")}
                onClick={() => {
                  dispatch(
                    setModalsData({
                      ...modalsState,
                      showConfirmMoveGroup: true,
                    })
                  );
                }}
                disabled={!moveToGroup}
              />
              <AdminFormFieldSubmit
                type="button"
                variant="danger"
                label={tMain("cancel")}
                onClick={() => {
                  setMovingGroup(false);
                  setParentOfMovingGroup(null);
                  setMoveToGroup(null);
                }}
              />
            </>
          )}
        </div>
      }
    />
  );
};
export default GroupHierarchyHeaderView;
